import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useMemo, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { ACTUAL_CLINIC_TYPES } from "constants/clinics";
import {
  CLINIC_SELECTOR_DISPLAY_NAME_FEATURE,
  REGIONS_SELECTOR_FEATURE
} from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentRegion,
  selectCurrentUser,
  selectHasRegions,
  selectHasServiceLocations,
  setCurrentClinic,
  setCurrentRegion,
  setHasServiceLocations,
  setUserClinics
} from "features/auth/auth-slice";
import {
  CLINICS_MINIMAL_QUERY,
  CLINICS_MINIMAL_WITH_OWN_DISPLAY_NAME_QUERY,
  useGetClinicsQuery,
  useLazyGetClinicQuery,
  useLazyGetClinicsQuery
} from "services/clinics";
import { useGetRegionsListQuery } from "services/regions";
import { ClinicTypes } from "services/types/clinics.type";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import ClinicSelector from "./clinic-selector.component";
import RegionsSelectorComponent from "./regions-selector/regions-selector.component";

type CurrentClinicSelectorProps = {
  isLargeScreen: boolean;
  minimalView?: boolean;
};

const REGIONS_QUERY =
  "{id,clinics{id,name,timezone,noah_provider,noah_alias,noah_tenant_id},created_at,name,is_active,noah_location_id}";

const CurrentClinicRegionSelector: React.FC<CurrentClinicSelectorProps> = ({
  isLargeScreen,
  minimalView = false
}) => {
  const flags = useFlags();
  const isRegionsSelectorEnabled = flags[REGIONS_SELECTOR_FEATURE];
  // Off => same request and labels as before. The request keys on this flag alone (not on the
  // regions flag), so the regions flag resolving after the first render does not refetch clinics.
  const isClinicDisplayNameEnabled = Boolean(
    flags[CLINIC_SELECTOR_DISPLAY_NAME_FEATURE]
  );

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const isShowOwnClinicsOnlyEnabled = currentUser.show_own_clinics_only;
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentRegion = useAppSelector(selectCurrentRegion);
  const hasRegionsInStore = useAppSelector(selectHasRegions);
  const hasServiceLocationsInStore = useAppSelector(selectHasServiceLocations);

  const localStorageClinic = setupLocalStorageAPI("selectedClinic");
  const { default_clinic: defaultClinic } = currentUser;
  const storedClinic =
    localStorageClinic.getData(defaultClinic) || defaultClinic;
  const currentPracticeId = currentUser?.practice?.id;

  // ---------- memoized args to avoid identity churn ----------
  const regionsArgs = useMemo(
    () => ({
      user: currentUser.id,
      is_active: true,
      query: REGIONS_QUERY
    }),
    [currentUser.id]
  );

  const clinicsArgs = useMemo(() => {
    const args: {
      query: string;
      type: typeof ACTUAL_CLINIC_TYPES;
      user?: number;
    } = {
      query: isClinicDisplayNameEnabled
        ? CLINICS_MINIMAL_WITH_OWN_DISPLAY_NAME_QUERY
        : CLINICS_MINIMAL_QUERY,
      type: ACTUAL_CLINIC_TYPES
    };

    // Only pass user parameter when regions are enabled OR showOwnClinicsOnly is enabled
    if (
      (isRegionsSelectorEnabled || isShowOwnClinicsOnlyEnabled) &&
      currentUser?.id
    ) {
      args.user = currentUser.id;
    }

    return args;
  }, [
    currentUser?.id,
    isRegionsSelectorEnabled,
    isShowOwnClinicsOnlyEnabled,
    isClinicDisplayNameEnabled
  ]);

  // (Lazy) service locations: only query on demand
  const serviceLocArgs = useMemo(() => {
    const args: {
      query: string;
      type: ClinicTypes[];
      user?: number;
    } = {
      query: CLINICS_MINIMAL_QUERY,
      type: [ClinicTypes.SERVICE_LOCATION]
    };

    // Only pass user parameter when regions are enabled OR showOwnClinicsOnly is enabled
    if (
      (isRegionsSelectorEnabled || isShowOwnClinicsOnlyEnabled) &&
      currentUser?.id
    ) {
      args.user = currentUser.id;
    }

    return args;
  }, [currentUser?.id, isRegionsSelectorEnabled, isShowOwnClinicsOnlyEnabled]);

  // ---------- queries ----------
  const {
    data: regions = [],
    isFetching: regionsIsFetching,
    isSuccess: isRegionsSuccess
  } = useGetRegionsListQuery(regionsArgs, {
    skip: !isRegionsSelectorEnabled || !currentUser.id || currentUser.id === -1
  });

  const { data: clinics = [], isSuccess: isClinicsSuccess } =
    useGetClinicsQuery(clinicsArgs, {
      skip: !currentUser?.id || currentUser.id === -1
    });

  const [triggerServiceLoc] = useLazyGetClinicsQuery();

  const [getClinicDetails] = useLazyGetClinicQuery();

  // ---------- derived ----------
  const userRegions = useMemo(() => {
    if (!isClinicsSuccess || !isRegionsSuccess || !isRegionsSelectorEnabled)
      return [];
    return regions.map((region) => ({
      ...region,
      clinics: clinics.filter((c) => c.region?.id === region.id)
    }));
  }, [
    regions,
    clinics,
    isClinicsSuccess,
    isRegionsSuccess,
    isRegionsSelectorEnabled
  ]);

  // Run the initial "pick current clinic" once per practice. Keyed to the
  // practice id (not a bare boolean) because the top bar stays mounted across a
  // practice switch: a switch clears currentClinic and relies on this effect to
  // re-select a clinic for the new practice. A boolean latch would never reset,
  // leaving single-clinic practices with no clinic selected and an unloadable
  // Scheduler (ENG-1519).
  const initDonePracticeRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      initDonePracticeRef.current === currentPracticeId ||
      !isClinicsSuccess ||
      // Wait for LaunchDarkly to resolve: an undefined flag during a practice switch would boot
      // without regions and latch `hasRegions: false` for the whole session (ENG-1519).
      isRegionsSelectorEnabled === undefined ||
      (isRegionsSelectorEnabled && regionsIsFetching) ||
      currentClinic ||
      !currentPracticeId ||
      clinics.length === 0
    ) {
      return;
    }

    // Does this practice have service locations? Drives the scheduler's "Show Service locations"
    // checkbox. One lazy call per practice (SN-14239), for every practice — it was gated on
    // regions, which hid the checkbox from practices without them (ENG-2101). The answer is
    // dropped if the user switched practice while it was in flight.
    const checkServiceLocations = async () => {
      const res = await triggerServiceLoc(serviceLocArgs);
      const list = ("data" in res && res.data) || [];
      dispatch((dispatch, getState) => {
        if (getState().auth.user?.practice?.id !== currentPracticeId) return;
        dispatch(setHasServiceLocations(list.length > 0));
      });
    };
    // Choose the clinic: the user's remembered choice, else their default clinic, else first by
    // name. The default-clinic middle step matters after a practice switch — the backend minted a
    // deterministic active-practice default clinic into the token, and the switch cleared the
    // remembered choice, so both sides agree on the same clinic (and therefore the same region in
    // the top-bar header) instead of each picking arbitrarily (ENG-1519).
    const updatedStoredClinic = clinics.find((c) => c.id === storedClinic?.id);
    const validStoredClinic =
      updatedStoredClinic?.practice?.id === currentPracticeId
        ? updatedStoredClinic
        : undefined;
    const validDefaultClinic = clinics.find((c) => c.id === defaultClinic?.id);
    const clinic = validStoredClinic ?? validDefaultClinic ?? clinics[0];
    // we can fire the lazy check in parallel; it's not critical to selecting the clinic
    void checkServiceLocations();

    // First, immediately set the clinic with minimal details
    dispatch(
      setCurrentClinic({
        currentClinic: clinic,
        numberOfClinics: clinics.length,
        hasServiceLocations: false, // provisional; checkServiceLocations sets the real value
        hasRegions: isRegionsSelectorEnabled && Boolean(regions.length > 0)
      })
    );

    // Then fetch and update with full clinic details
    if (clinic?.id) {
      const selectedClinicId = clinic.id;
      getClinicDetails({ clinicId: selectedClinicId })
        .then((result) => {
          if (result.data) {
            // Only update if this is still the currently selected clinic
            dispatch((dispatch, getState) => {
              const { currentClinic: selected, hasServiceLocations } =
                getState().auth;
              if (selected?.id === selectedClinicId) {
                dispatch(
                  setCurrentClinic({
                    currentClinic: result.data,
                    numberOfClinics: clinics.length,
                    // Keep the service-locations answer if it landed first (ENG-2101).
                    hasServiceLocations,
                    hasRegions:
                      isRegionsSelectorEnabled && Boolean(regions.length > 0)
                  })
                );
              }
            });
          }
        })
        .catch((error) => {
          // If fetching full details fails, we still have the minimal details set
          console.error("Failed to fetch full clinic details:", error);
        });
    }

    initDonePracticeRef.current = currentPracticeId;
  }, [
    regionsIsFetching,
    isClinicsSuccess,
    currentClinic,
    currentPracticeId,
    clinics,
    storedClinic,
    defaultClinic,
    dispatch,
    regions.length,
    isRegionsSelectorEnabled,
    triggerServiceLoc,
    serviceLocArgs,
    getClinicDetails
  ]);

  // hasRegions can be latched wrong when the LD flag resolves only after boot (the LD context is
  // re-identified asynchronously during a practice switch, so the flag briefly holds the previous
  // practice's value). Re-sync the store once the flag and regions settle so region-aware pages
  // (Inbox, Patients) don't stay in clinic-only mode for the whole session (ENG-1519).
  useEffect(() => {
    if (!currentClinic || isRegionsSelectorEnabled === undefined) return;
    if (isRegionsSelectorEnabled && !isRegionsSuccess) return;
    const computedHasRegions =
      Boolean(isRegionsSelectorEnabled) && regions.length > 0;
    if (computedHasRegions === hasRegionsInStore) return;
    dispatch(
      setCurrentClinic({
        currentClinic,
        numberOfClinics: clinics.length,
        hasServiceLocations: hasServiceLocationsInStore,
        hasRegions: computedHasRegions
      })
    );
  }, [
    currentClinic,
    isRegionsSelectorEnabled,
    isRegionsSuccess,
    regions.length,
    hasRegionsInStore,
    hasServiceLocationsInStore,
    clinics.length,
    dispatch
  ]);

  // When currentClinic moves across regions, keep currentRegion in sync (idempotent)
  useEffect(() => {
    if (!currentClinic?.region || !isRegionsSelectorEnabled) return;

    const needUpdate = currentRegion?.id !== currentClinic.region.id;
    if (!needUpdate) return;

    const updatedRegion = userRegions.find(
      (r) => r.id === currentClinic.region?.id
    );
    if (updatedRegion) {
      dispatch(setCurrentRegion(updatedRegion));
    } else {
      dispatch(setCurrentRegion(null));
    }
  }, [
    dispatch,
    currentClinic,
    currentRegion,
    userRegions,
    isRegionsSelectorEnabled
  ]);

  // Set user clinics when show_own_clinics_only is enabled for the user
  useEffect(() => {
    if (isShowOwnClinicsOnlyEnabled && isClinicsSuccess && clinics.length > 0) {
      dispatch(setUserClinics(clinics));
    }
  }, [isShowOwnClinicsOnlyEnabled, isClinicsSuccess, clinics, dispatch]);

  if (regionsIsFetching || isRegionsSelectorEnabled === undefined) {
    return null;
  }

  return isRegionsSelectorEnabled && regions.length ? (
    <RegionsSelectorComponent
      minimalView={minimalView}
      regions={regions}
      clinics={clinics}
    />
  ) : (
    <ClinicSelector
      clinics={clinics}
      isLargeScreen={isLargeScreen}
      minimalView={minimalView}
      // Practices without the regions selector only. Everyone, Suno staff included, sees the same
      // labels as the clinic's own staff.
      showDisplayNames={
        isClinicDisplayNameEnabled && isRegionsSelectorEnabled === false
      }
    />
  );
};

export default CurrentClinicRegionSelector;
